import React, { useState, useEffect } from 'react';
import { Flex, Button, Text, Group, Progress, Loader } from '@mantine/core';
import { useIntl } from 'react-intl';
import { IoCallOutline, IoMailOutline } from 'react-icons/io5';
import { IoIosCloseCircleOutline } from 'react-icons/io';
import { useSelector } from 'react-redux';
import { getProfileData } from '../../store/profile.slice';
import {
  CheckListMenuWrapper,
  CheckListButton,
  CheckListDropdown,
  CheckListHeader,
  CheckListOption,
} from './styles';
import { getLangState } from '../../store/lang.slice';
import { BsCheckCircleFill } from 'react-icons/bs';
import { FaRegCircle } from 'react-icons/fa';
import { ReactComponent as EventsIcon } from '../../assets/images/svg/link-event.svg';
import { ReactComponent as AppointmentIcon } from '../../assets/images/svg/calendar-event.svg';
import { useNavigate } from 'react-router-dom';
import { PiRocketLight } from 'react-icons/pi';
import { useMediaQuery } from '@mantine/hooks';

interface CheckListOption {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  checked: boolean;
  collapsed: boolean;
  route: string;
  actionText: string;
}

const GlobalCheckListMenu: React.FC = () => {
  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const lang = useSelector(getLangState);
  const isRTL = lang === 'ar';
  const navigate = useNavigate();
  const profile = useSelector(getProfileData);
  const [CheckListOptions, setCheckListOptions] = useState<CheckListOption[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useMediaQuery('(max-width: 767px)');

  // Initialize and update CheckListOptions when profile data changes
  useEffect(() => {
    if (
      !profile ||
      profile.id === -1 ||
      !profile.role ||
      profile.role.role_value === undefined
    ) {
      setIsLoading(true);
      return;
    }

    const baseOptions: CheckListOption[] = [
      {
        id: 'events',
        title: formatMessage({ id: 'createFirstEventTitle' }),
        description: formatMessage({ id: 'createFirstEventDesc' }),
        icon: <EventsIcon />,
        checked: profile?.client_system_Steps?.create_first_event || false,
        collapsed: true,
        route: '/events/add-event',
        actionText: formatMessage({ id: 'addEventBtn' }),
      },
      ...(profile.role.role_value !== 2
        ? [
            {
              id: 'members',
              title: formatMessage({ id: 'addMembersTitle' }),
              description: formatMessage({ id: 'addMembersDesc' }),
              icon: <AppointmentIcon />,
              checked:
                profile?.client_system_Steps?.create_first_client || false,
              collapsed: true,
              route: !isMobile ? '/settings/members' : '/members',
              actionText: formatMessage({ id: 'manageMembersButton' }),
            },
          ]
        : []),
      {
        id: 'google-calendar',
        title: formatMessage({ id: 'calendarIntegrationTitle' }),
        description: formatMessage({ id: 'calendarIntegrationDesc' }),
        icon: <IoCallOutline size={20} />,
        checked: profile?.client_system_Steps?.integrate_with_calendar || false,
        collapsed: true,
        route: !profile?.client_system_Steps?.integrate_with_calendar
          ? '/settings/integration/activate-google-calendar'
          : '/settings/calendar-sync',
        actionText: formatMessage({ id: 'integrationTitle' }),
      },
      {
        id: 'gmail',
        title: formatMessage({ id: 'gmailIntegrationTitle' }),
        description: formatMessage({ id: 'gmailIntegrationDesc' }),
        icon: <IoMailOutline size={20} />,
        checked: profile?.client_system_Steps?.integrate_with_gmail || false,
        collapsed: true,
        route: !profile?.client_system_Steps?.integrate_with_gmail
          ? '/settings/integration/activate-gmail'
          : '/settings/integration/activate-gmail?is_active=true',
        actionText: formatMessage({ id: 'integrationTitle' }),
      },

      // {
      //   id: 'workflow',
      //   title: formatMessage({ id: 'workflowTitle' }),
      //   description: formatMessage({ id: 'workflowDesc' }),
      //   icon: <IoMailOutline size={20} />,
      //   checked: profile?.client_system_Steps?.create_first_workflow || false,
      //   collapsed: true,
      //   route: '/workflows',
      //   actionText: formatMessage({ id: 'addWorkflowBtn' }),
      // },

    ];

    setCheckListOptions(baseOptions);
    setIsLoading(false);
  }, [
    profile,
    profile?.id,
    profile?.role?.role_value,
    profile?.client_system_Steps,
    formatMessage,
    isMobile,
  ]);

  const handleOptionClick = (optionId: string) => {
    setCheckListOptions((prev) =>
      prev.map((option) =>
        option.id === optionId
          ? { ...option, collapsed: !option.collapsed }
          : { ...option, collapsed: true }
      )
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isOpen && !target.closest('[data-CheckList-menu]')) {
        setIsOpen(false);
        setCheckListOptions((prev) =>
          prev.map((option) => ({ ...option, collapsed: true }))
        );
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <CheckListMenuWrapper isRTL={isRTL} data-CheckList-menu>
      <CheckListButton
        onClick={() => setIsOpen(!isOpen)}
      >
        <Flex align="center" gap="1px">
          <PiRocketLight size={16} />
          <Text
            fw={500}
            lh={'13px'}
            style={{
              color: 'white',
              fontSize: '13px',
            }}
          >
            {isLoading
              ? <Loader variant="dots" size="xs" color="white" />
              : `${
                  CheckListOptions.filter((option) => option.checked).length
                }/${CheckListOptions.length}`}
          </Text>
        </Flex>
      </CheckListButton>

      {isOpen && (
        <CheckListDropdown isRTL={isRTL}>
          <CheckListHeader>
            <Flex
              align="center"
              h={'14px'}
              gap="6px"
              justify="space-between"
              w="100%"
            >
              <Text size={'14px'} fw={500} lh={'14px'}>
                {formatMessage({ id: 'startWithJadwalh' })}
              </Text>
              <Button
                variant="subtle"
                color="gray"
                size="16px"
                onClick={() => setIsOpen(false)}
                p="0"
              >
                <IoIosCloseCircleOutline size={16} />
              </Button>
            </Flex>
            <Progress
              w="100%"
              value={
                isLoading
                  ? 0
                  : (CheckListOptions.filter((option) => option.checked)
                      .length /
                      CheckListOptions.length) *
                    100
              }
              size="xs"
              color="#303030"
            />
          </CheckListHeader>

          <div style={{ padding: '16px' }}>
            <Group spacing="12px">
              {CheckListOptions.map((option) => (
                <CheckListOption key={option.id}>
                  <Flex
                    align="flex-start"
                    gap="12px"
                    style={{
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                    }}
                    onClick={() => handleOptionClick(option.id)}
                  >
                    <div
                      style={{
                        color: option.checked ? '#303030' : '#c1c1c1',
                        transition: 'color 0.2s ease',
                        cursor: 'pointer',
                        height: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {option.checked ? (
                        <BsCheckCircleFill size={16} />
                      ) : (
                        <FaRegCircle size={16} />
                      )}
                    </div>
                    <Flex gap="12px" direction="column">
                      <Text
                        align="right"
                        color="#303030"
                        size="13px"
                        fw={500}
                        lh={'16px'}
                      >
                        {option.title}
                      </Text>
                      <Text size="12px" c="dimmed">
                        {option.description}
                      </Text>
                      {!option.collapsed && (
                        <Button
                          variant="outline"
                          h={'32px'}
                          fw={500}
                          size="14px"
                          color="#303030"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(option.route);
                          }}
                        >
                          {option.actionText}
                        </Button>
                      )}
                    </Flex>
                  </Flex>
                </CheckListOption>
              ))}
            </Group>
          </div>
        </CheckListDropdown>
      )}
    </CheckListMenuWrapper>
  );
};

export default GlobalCheckListMenu;
